import { useState, useEffect } from "react";
import { Button, Badge, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Modal } from "./SwipeableModal";

const WhatsNewModal = ({ show, onHide, version }) => {
  const { t } = useTranslation();
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && version) {
      fetchChangelog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, version]);

  const fetchChangelog = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/changelog.json");
      if (!response.ok) {
        setError(t("whatsNew.errors.fetchFailed"));
      }
      const data = await response.json();

      // Find the changelog entry for this version
      const versionChangelog = data.versions.find((v) => v.version === version);
      setChangelog(versionChangelog);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const variants = {
      feature: "success",
      improvement: "info",
      fix: "warning",
      security: "danger",
    };
    return variants[type] || "purple";
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="whats-new-modal"
    >
      <div className="changelog-header d-flex flex-column align-items-center p-4 rounded-top-3">
        <div className="bg-white rounded-4 px-3 py-0 d-flex align-items-center shadow-sm my-3 z-2">
          <i className="bi bi-stars fs-3 text-yellow me-1"></i>
          <span className="fw-bold fs-5 text-purple">{t("Version")}</span>
          <span className="fw-bold fs-5 text-purple ms-1">
            {changelog?.version}
          </span>
        </div>

        <h1 className="h2 fw-bold z-2">{t("whatsNew.title")}</h1>
        <p className="z-2">
          {t("whatsNew.subtitle")}
        </p>
      </div>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t("Loading...")}</span>
            </Spinner>
          </div>
        )}

        {error && (
          <div className="text-center py-4">
            <i className="bi bi-exclamation-triangle fs-1 text-warning mb-3"></i>
            <p className="text-muted">{t("Unable to load changelog.")}</p>
          </div>
        )}

        {changelog && changelog.changes && changelog.changes.length > 0 && (
          <div className="d-flex flex-column list-group list-group-flush">
            {changelog.changes.map((change, index) => (
              <div
                key={index}
                className="release-item card mb-3 border p-3"
                style={{
                  "--bs-release-color": `var(--bs-${getTypeBadge(
                    change.type
                  )})`,
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      {change.title && (
                        <h5 className="fw-semibold mb-1">{change.title}</h5>
                      )}
                    </div>
                    <p className="mb-0 text-muted">{change.description}</p>
                  </div>
                  <Badge
                    bg={getTypeBadge(change.type)}
                    className="me-2 text-capitalize"
                  >
                    {t(change.type)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          {t("whatsNew.awesome")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WhatsNewModal;
